import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { staffService } from '../../../services/staffService';
import { useAuth } from '../../../contexts/AuthContext';

const StaffPasswordModal = ({ isOpen, onClose, staffMember }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm the password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const { success, error } = await staffService?.setStaffPassword(
        staffMember?.id, 
        formData?.password, 
        userProfile
      );

      if (error) {
        setErrors({ general: error });
        return;
      }

      if (success) {
        onClose?.();
        // You might want to show a success notification here
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ password: '', confirmPassword: '' });
    setErrors({});
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Set Password</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Set password for {staffMember?.user_profiles?.full_name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Message */}
          {errors?.general && (
            <div className="bg-error/10 border border-error/20 text-error p-3 rounded-lg text-sm">
              {errors?.general}
            </div>
          )}

          {/* Staff Info */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Lock size={20} className="text-primary-foreground" />
              </div>
              <div>
                <p className="font-medium text-foreground">
                  {staffMember?.user_profiles?.full_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {staffMember?.user_profiles?.email}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {staffMember?.staff_role?.replace('_', ' ')} • {staffMember?.status}
                </p>
              </div>
            </div>
          </div>

          {/* Password Fields */}
          <div className="space-y-4">
            <div className="relative">
              <Input
                label="New Password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter new password"
                value={formData?.password || ''}
                onChange={handleInputChange}
                error={errors?.password}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <div className="relative">
              <Input
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData?.confirmPassword || ''}
                onChange={handleInputChange}
                error={errors?.confirmPassword}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className="bg-muted/30 p-3 rounded-lg">
            <p className="text-xs text-muted-foreground mb-2">Password Requirements:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li className={`flex items-center space-x-2 ${
                formData?.password?.length >= 6 ? 'text-green-600' : ''
              }`}>
                <span>•</span>
                <span>At least 6 characters long</span>
              </li>
              <li className={`flex items-center space-x-2 ${
                formData?.password && formData?.password === formData?.confirmPassword ? 'text-green-600' : ''
              }`}>
                <span>•</span>
                <span>Passwords must match</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              className="flex-1"
            >
              Set Password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffPasswordModal;